import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { apiFetch } from "../lib/http-client.js";
import { loadSeenLeadIds, pickNewlyArrived, saveSeenLeadIds } from "../lib/lead-notifications.js";

type NewLead = { id: string; name: string | null; phone: string };
type ToastItem = { id: string; leadId: string; text: string };

const POLL_INTERVAL_MS = 20000;
const TOAST_AUTO_DISMISS_MS = 8000;

/**
 * Montado uma única vez em App.tsx (irmão de <Routes>, não dentro de
 * StaffLayout) pra sobreviver à navegação entre páginas — é o que permite
 * o alerta de "lead novo" tocar em qualquer tela do painel, não só no funil.
 */
export function LeadNotificationsWatcher() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seenIdsRef = useRef<Set<string> | null>(null);
  const queryClient = useQueryClient();

  const { data, dataUpdatedAt } = useQuery({
    queryKey: ["leads", "new-watch"],
    queryFn: () => apiFetch<{ items: NewLead[] }>("/leads?stage=NEW"),
    enabled: isAuthenticated,
    refetchInterval: POLL_INTERVAL_MS,
  });

  useEffect(() => {
    if (!data) return;

    const currentIds = data.items.map((lead) => lead.id);

    if (seenIdsRef.current === null) {
      // Primeira carga da sessão: estabelece a base a partir do que já está
      // salvo localmente (ou, na ausência disso, dos leads NEW atuais) sem
      // notificar nada — só o que chegar DEPOIS disso deve tocar o alerta.
      const stored = loadSeenLeadIds();
      seenIdsRef.current = stored.size > 0 ? stored : new Set(currentIds);
      saveSeenLeadIds([...seenIdsRef.current]);
      return;
    }

    const newIds = pickNewlyArrived(currentIds, seenIdsRef.current);
    if (newIds.length === 0) return;

    for (const id of newIds) seenIdsRef.current.add(id);
    saveSeenLeadIds([...seenIdsRef.current]);

    new Audio("/sounds/notification.wav").play().catch(() => {
      // Autoplay pode ser bloqueado até o usuário interagir com a página —
      // limitação do navegador, não um bug.
    });

    const newLeads = data.items.filter((lead) => newIds.includes(lead.id));
    setToasts((prev) => [
      ...prev,
      ...newLeads.map((lead) => ({
        id: `${lead.id}-${Date.now()}`,
        leadId: lead.id,
        text: `Novo lead: ${lead.name ?? lead.phone}`,
      })),
    ]);
  }, [data]);

  // dataUpdatedAt (não data): muda a cada fetch bem-sucedido do polling
  // acima, mesmo quando o conteúdo retornado é idêntico ao anterior (o
  // React Query mantém `data` na mesma referência nesse caso, via
  // structural sharing) — dependendo de `data` aqui deixaria de invalidar
  // num poll onde a lista de NEW não mudou, mas outro lead trocou de etapa
  // (o caso que motivou esta task).
  useEffect(() => {
    if (dataUpdatedAt === 0) return; // ainda não buscou nada nesta sessão
    queryClient.invalidateQueries({
      queryKey: ["leads"],
      // Exclui a própria query do watcher (["leads", "new-watch"]): ela cai
      // dentro do prefixo ["leads"] que este efeito invalida, e por padrão
      // invalidateQueries refaz o fetch de toda query ativa que casar — sem
      // este predicate, o refetch da própria query geraria um novo
      // dataUpdatedAt, que dispararia este efeito de novo, em loop, na
      // velocidade do round-trip de rede (confirmado: 64 fetches em 2s).
      predicate: (query) => query.queryKey[1] !== "new-watch",
    });
  }, [dataUpdatedAt, queryClient]);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((toast) =>
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== toast.id)), TOAST_AUTO_DISMISS_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  if (!isAuthenticated || toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="flex items-center gap-3 rounded-lg border border-sand bg-white px-4 py-3 text-sm text-ink shadow-md"
        >
          <button
            type="button"
            className="text-left hover:underline"
            onClick={() => {
              setToasts((prev) => prev.filter((t) => t.id !== toast.id));
              navigate(`/leads?leadId=${encodeURIComponent(toast.leadId)}`);
            }}
          >
            {toast.text}
          </button>
          <button
            type="button"
            aria-label="Fechar"
            className="text-ink-300 hover:text-ink"
            onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
